import React from "react";
import { Icon, message, Tooltip } from "antd";
import CountUp from "react-countup";
import { withTranslation } from "react-i18next";
import { compose, withHandlers } from "recompose";
import i18n from "i18next";
import copy from "copy-to-clipboard";

import styled from "styled-components/macro";

const getCountFontSize = value => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) return "4em";

  const digits = Math.trunc(Math.abs(numericValue)).toString().length;
  if (digits >= 11) return "2.5em";
  if (digits >= 8) return "3.2em";
  if (digits >= 5) return "3.85em";

  return "4em";
};

const CountCard = styled.article`
  display: flex;
  flex-direction: ${props => (props.listMode ? "row-reverse" : "row")};
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.15);
  transition: 0.3s ease;
  flex-wrap: wrap;
  border-radius: 6px !important;
  justify-content: ${props => (props.listMode ? "flex-start" : "center")};
  width: ${props => (props.listMode ? "29%" : "100%")};
  max-width: ${props => (props.listMode ? "90px" : "240px")};
  margin: ${props => (props.listMode ? "5px" : "20px 0")};
  padding: ${props => (props.listMode ? "5px 10px" : "20px")};
  position: relative;
  opacity: 0;
  transform: translateY(10px);
  max-height: ${props => props.listMode && "120px"};
  animation: fadeIn 0.3s ease forwards;
  justify-content: ${props => props.listMode && "center"};
  animation-delay: ${props => `${props.delay}s !important`};
  @keyframes fadeIn {
    0% {
      opacity: 0;
      transform: translateY(10px);
    }
    100% {
      opacity: 1;
      transform: translateY(0);
    }
  }
  &:hover {
    box-shadow: 0px 8px 15px -3px gainsboro;
  }
  @media print {
    opacity: 1;
  }
`;

const CountStyle = styled.span`
  display: flex;
  align-items: baseline;
  justify-content: center;
  font-size: ${props => (props.listMode ? "1.5em" : props.fontSize || "4em")};
  color: ${props => props.color};
  font-weight: bold;
  width: 100%;
  margin: ${props => (props.listMode ? "0 auto" : "10px 0")};
  overflow: visible;
  line-height: 1;
  white-space: nowrap;
  text-align: center;
  text-overflow: clip;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.04em;
`;

const LabelCard = styled.p`
  color: ${({ theme }) => theme["@grey-medium"]};
  font-size: 0.85em;
  margin: 5px 0;
  text-align: ${props => props.listMode && "center"};
`;

const Circle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  top: -20px;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  position: absolute;
  background: white;
  box-shadow: 0px -2px 5px -3px gainsboro;
  font-size: 1.5em;
  color: ${({ theme }) => theme["@primary"]};
  > i {
    transform: translateY(0);
    transition: 0.5s ease;
  }
  overflow: hidden;
  ${CountCard}:hover & {
    > i {
      transform: translateY(50px);
    }
  }
`;

const CopyData = styled.button`
  display: flex;
  position: absolute;
  transform: translateY(-50px);
  background: white;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme["@primary"]};
  font-size: 1em;
  border-radius: 50%;
  transition: 0.3s ease;
  cursor: pointer;
  border: none;
  &:active,
  :focus {
    outline: none;
  }
  ${CountCard}:hover & {
    transform: translateY(0px);
  }
`;
const Suffix = styled.span`
  font-size: 0.25em;
  color: ${({ theme }) => theme["@primary"]};
  margin-right: -31px;
  margin-left: 5px;
`;
const AnalyticsCard = ({
  icon,
  number,
  duration,
  delay,
  label,
  node,
  copyToClipboard,
  i18n,
  listMode,
  color,
  hideCountUp,
  suffix,
  dataTest
}) => {
  return (
    <CountCard delay={delay} listMode={listMode} data-test={dataTest}>
      {node || listMode || (
        <Circle>
          <Tooltip
            placement="top"
            title={i18n.t("analytics:copyToClipboard.tooltip")}
          >
            <CopyData onClick={() => copyToClipboard(label, number)}>
              <Icon type="copy" style={{ color: color }} />
            </CopyData>
          </Tooltip>
          <Icon type={icon} style={{ color: color }} />
        </Circle>
      )}
      <CountStyle
        color={color}
        listMode={listMode}
        fontSize={getCountFontSize(number)}
      >
        {hideCountUp ? (
          number
        ) : (
          <CountUp end={number} duration={duration} delay={delay} />
        )}
        {suffix && <Suffix>{suffix}</Suffix>}
      </CountStyle>
      <LabelCard listMode={listMode}>{label}</LabelCard>
    </CountCard>
  );
};

const enhacer = compose(
  withTranslation(["analytics"]),
  withHandlers({
    copyToClipboard: ({ number, label }) => () => {
      copy(`${label}: ${number}`);
      message.success(i18n.t("analytics:copyToClipboard.success"));
    }
  })
);

export default enhacer(AnalyticsCard);
